import { CaretDownFilled } from '@ant-design/icons'
import { Card, Dropdown, Menu, Space, Spin } from 'antd'
import dayjs from 'dayjs'
import ReactECharts, { EChartsOption } from 'echarts-for-react'
import React, { useState } from 'react'
import { EventReportKeyColumnDescription } from 'src/api/api'
import { formatCompact, formatLocale } from 'src/util/Util'
import { PerformanceData, Period } from './useDashboardData'

const KeyColumnEnum = EventReportKeyColumnDescription.ColumnEnum

interface PerformanceChartProps {
    data: PerformanceData
    period: Period
    loading: boolean
}

const metrics = {
    impression: { label: 'Impressions', toUnit: undefined },
    click: { label: 'Clicks', toUnit: undefined },
    cost: { label: 'Cost', toUnit: v => '$' + v },
    ctr: { label: 'CTR', toUnit: v => v + '%' },
    ecpm: { label: 'eCPM', toUnit: v => '$' + v }
}

const dayFormatter = 'D MMM'
const hourFormatter = 'HH:mm'

const colors = ['#5E81B8', '#E9204F', '#10B981', '#EF4444', '#6366F1', '#EC4899']

const PerformanceChart: React.FC<PerformanceChartProps> = ({ data, period, loading }) => {

    const [selectedMetrics, setSelectedMetrics] = useState<string[]>(['cost', 'impression'])

    const xAxisDateFormat = period.key == KeyColumnEnum.Day ? dayFormatter : hourFormatter

    const handleMetricReplace = (index: number, newMetric: string) => {
        const newSelection = [...selectedMetrics]
        newSelection[index] = newMetric
        setSelectedMetrics(newSelection)
    }

    const renderMetricSelector = (selectedMetric: string, index: number) => {
        const menu = <Menu
            onClick={({ key }) => handleMetricReplace(index, key)}
            items={Object.keys(metrics)
                .filter(metric => !selectedMetrics.includes(metric) || metric === selectedMetric)
                .map(metric => ({
                    key: metric,
                    label: metrics[metric].label
                }))}
        />

        return <Dropdown overlay={menu} trigger={['click']} key={selectedMetric} >
            <div style={{
                display: 'flex',
                alignItems: 'center',
                marginLeft: 12,
                cursor: 'pointer',
                userSelect: 'none'
            }}>
                <div style={{
                    width: 10,
                    height: 10,
                    backgroundColor: colors[index % colors.length],
                    marginRight: 8,
                    borderRadius: 2
                }} />
                <span style={{ fontWeight: 500 }}>{metrics[selectedMetric].label}</span>
                <CaretDownFilled style={{ fontSize: 12, marginLeft: 4 }} />
            </div>
        </Dropdown>
    }

    const option: EChartsOption = {
        tooltip: {
            trigger: 'axis' as const,
            formatter: (params: any) => {
                const index = params[0].dataIndex
                const date = data.dateTime[index] // original timestamp
                const formattedDate = dayjs(date).format('YYYY-MM-DD HH:mm')

                // Build custom lines
                const lines = params.map((p: any, i: number) => {
                    const metric = selectedMetrics[i]
                    const valueArray = data[metric as keyof PerformanceData]
                    const originalValue = Array.isArray(valueArray) && typeof valueArray[index] === 'number' ? (valueArray[index] as number) : 0
                    const absolute = formatLocale(originalValue, metrics[metric].toUnit) // formatted raw value

                    return `<div>
                                <span style="display:inline-blockmargin-right:4pxborder-radius:2pxwidth:10pxheight:10pxbackground:${p.color}"></span>
                                <strong>${p.seriesName}</strong>: ${absolute}
                            </div>`
                })
                return `<div>
                            <strong>${formattedDate}</strong>
                            <br/>
                            ${lines.join('')}
                        </div>`
            }
        },
        legend: {
            show: false
        },
        grid: {
            top: 10,
            left: 80,
            right: 80,
            bottom: 60
        },
        xAxis: {
            type: 'category' as const,
            boundaryGap: false,
            data: data.dateTime.map((ts) => dayjs(ts).format(xAxisDateFormat)),
            splitLine: {
                show: true,
                lineStyle: {
                    type: 'dashed',
                    color: '#ddd'
                },
                interval: data.dateTime.length > 40 ? 'auto' : 0
            },
            axisLabel: {
                interval: data.dateTime.length > 40 ? 'auto' : 0,
                hideOverlap: true
            },
            axisTick: {
                alignWithLabel: true,
                interval: data.dateTime.length > 40 ? 'auto' : 0
            }
        },
        yAxis: [
            {
                type: 'value' as const,
                name: metrics[selectedMetrics[0]].label,
                position: 'left',
                axisLabel: {
                    formatter: (value: number, _: number) => {
                        const raw = data[selectedMetrics[0] as keyof PerformanceData] as number[]
                        if (!raw) {
                            return ''
                        }
                        const max = Math.max(...raw)
                        return formatCompact(value * max, metrics[selectedMetrics[0]].toUnit)
                    }
                },
                splitLine: {
                    show: true,
                    lineStyle: {
                        type: 'dashed',
                        color: '#ddd'
                    }
                }
            },
            {
                type: 'value' as const,
                name: metrics[selectedMetrics[1]].label,
                position: 'right',
                axisLabel: {
                    formatter: (value: number, _: number) => {
                        const raw = data[selectedMetrics[1] as keyof PerformanceData] as number[]
                        if (!raw) {
                            return ''
                        }
                        const max = Math.max(...raw)
                        return formatCompact(value * max, metrics[selectedMetrics[1]].toUnit)
                    }
                },
                splitLine: { show: false }
            }
        ],
        series: selectedMetrics.map((metric, index) => {

            const value = data[metric as keyof PerformanceData] ?? []
            const rawData: number[] = Array.isArray(value) && value.every((v) => typeof v === 'number') ? (value as number[]) : []
            const maxVal = Math.max(...rawData.map((v) => Number(v) || 0))
            const normalizedData = maxVal > 0 ? rawData.map((v) => (Number(v) || 0) / maxVal) : rawData

            return {
                name: metrics[metric].label ?? metric,
                type: 'line' as const,
                smooth: false,
                showSymbol: false,
                data: data.dateTime.map((_, i) => normalizedData[i]) as number[],
                yAxisIndex: index, // <--- assign axis index: 0 (left), 1 (right)
                lineStyle: { color: colors[index % colors.length] },
                itemStyle: { color: colors[index % colors.length] }
            }
        })
    }

    return <Card style={{ borderRadius: 8, width: '100%', margin: '8px 8px 16px 8px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
            <div style={{ fontSize: 16, fontWeight: 600, paddingLeft: 4 }}>Performance</div>
            <Space>{selectedMetrics.map((metric, index) => renderMetricSelector(metric, index))}</Space>
        </div>

        {loading
            ? <Spin size="large" style={{ display: 'block', margin: '20px', minHeight: '380px', alignContent: 'center' }} />
            : <ReactECharts key={selectedMetrics.join(',')} option={option} style={{ height: '400px', width: '100%' }} notMerge={true} lazyUpdate={true} />}
    </Card>
}

export default PerformanceChart


// WEBPACK FOOTER //
// ./src/component/app/dashboard/PerformanceChart.tsx